'use client'

import React, { useMemo, useState } from 'react'
import { XMasonry, XBlock } from 'react-xmasonry'
import type {
  GameState,
  GameQuery,
  GameMarkets,
  Market,
} from '@azuro-org/toolkit'
import cx from 'classnames'

import { Icon } from 'components/ui'
import EmptyContent from 'compositions/EmptyContent/EmptyContent'

import Headline from './components/Headline/Headline'

import useView from './utils/useView'
import { useOvertimeCollapse } from './utils/useCollapse'

import { groupBy, orderBy } from 'lodash'
import type { SportMarket } from '@/Overtime/types/markets'
import { isFuturesMarket, MarketType } from 'overtime-utils'
import { getPositionTextV2, getTitleText } from '@/Overtime/utils/marketsV2'
import OvertimeOutcomeButton from '@/compositions/OutcomeButton/OvertimeOutcomeButton'
import { getGridMinMaxPercentage } from '@/helpers/getters/getGridMinMaxPercentage'

export const MarketsSkeleton: React.FC = () => {
  return (
    <div>
      <div className="bone rounded-full w-full h-[2.875rem]" />
      {new Array(3).fill(0).map((_, index) => (
        <div key={index}>
          <div className="flex items-center justify-between p-4">
            <div className="bone h-[1.125rem] w-24 rounded-full" />
          </div>
          <div className="space-y-2 bg-accent-grey-850 rounded-sm p-2">
            <div className="flex justify-between">
              <div className="flex gap-2 w-full">
                <div className="bone h-10 w-full rounded-min" />
                <div className="bone h-10 w-full rounded-min" />
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  )
}

type ConditionProps = {
  condition: Market['conditions'][0]
  marketName: string
  game: NonNullable<GameQuery['game']>
  betsSummary?: Record<string, string>
  isResult?: boolean
  isGameOpen: boolean
}

export const Condition: React.FC<any> = props => {
  const { condition, marketName, isGameOpen, market } = props

  const oddsInfo = condition.odds.map((odd: number, index: number) => {
    const position = index

    return {
      odd,
      position,
      positionName: condition.positionNames
        ? condition.positionNames[position]
        : '',
    }
  })

  const sortedOddsInfo = orderBy(oddsInfo, ['odd', 'position'], ['desc', 'asc'])
  const isFutures = isFuturesMarket(condition.typeId)
  const oddsForDisplay = isFutures ? sortedOddsInfo : oddsInfo
  const filteredOdds = oddsForDisplay

  const gridMinMaxPercentage = getGridMinMaxPercentage(condition, false)

  return (
    <div
      className="grid gap-2"
      style={{
        gridTemplateColumns: `repeat(auto-fit, minmax(calc(${gridMinMaxPercentage}% - 5px), 1fr))`,
      }}
    >
      {filteredOdds.map((outcome, index) => {
        const key = outcome.position
        const position = isFutures
          ? oddsInfo.findIndex(
              oddInfo =>
                condition.positionNames &&
                oddInfo.positionName === filteredOdds[index].positionName
            )
          : index

        const positionText = getPositionTextV2(condition, position, true)
        const isZeroOdd =
          !outcome.odd ||
          outcome.odd == 0 ||
          condition.typeId === MarketType.EMPTY

        return (
          <OvertimeOutcomeButton
            key={key}
            marketName={marketName}
            outcome={outcome}
            positionText={positionText}
            position={position}
            playerProps={condition.playerProps}
            isZeroOdd={isZeroOdd}
            market={condition}
            size={40}
            isGameOpen={isGameOpen}
            fullMarket={market}
          />
        )
      })}
    </div>
    // </div>
  )
}

type ContentProps = {
  market: GameMarkets
  game: NonNullable<GameQuery['game']>
  betsSummary?: Record<string, string>
  isResult?: boolean
  marketTypesFilter?: MarketType[]
}

const Content: React.FC<any> = props => {
  const { market, groupedChildMarkets, marketTypesFilter = [] } = props

  const { areAllCollapsed, collapsedMarketIds, collapse, collapseAll } =
    useOvertimeCollapse(groupedChildMarkets, market.typeId)

  const { activeView, changeView } = useView()

  const isGameStarted = market.maturityDate < new Date()
  const isGameOpen = market.isOpen && !isGameStarted

  return (
    <div className="bg-[#0F0E0D] -mx-2">
      <Headline
        activeView={activeView}
        isCollapsed={areAllCollapsed}
        onChangeView={changeView}
        onCollapse={collapseAll}
      />
      <div className="">
        <XMasonry maxColumns={10} targetBlockWidth={478}>
          <XBlock key="main-market" width={activeView === 'columns' ? 1 : 2}>
            <MarketBlockItem
              typeId={market.typeId}
              market={market}
              onCollapse={collapse}
              childMarkets={[market]}
              collapsedMarketIds={collapsedMarketIds}
              isGameOpen={isGameOpen}
            />
          </XBlock>
          {Object.keys(groupedChildMarkets).map((key, index) => {
            const typeId = Number(key)
            const childMarkets = groupedChildMarkets[typeId]

            return (
              <XBlock
                key={`child-market-${key}`}
                width={activeView === 'columns' ? 1 : 2}
              >
                <MarketBlockItem
                  typeId={Number(key)}
                  market={market}
                  onCollapse={collapse}
                  childMarkets={childMarkets}
                  collapsedMarketIds={collapsedMarketIds}
                  isGameOpen={isGameOpen}
                />
              </XBlock>
            )
          })}
        </XMasonry>
      </div>
    </div>
  )
}

interface MarketBlockItemProps {
  typeId: number
  market: Market
  onCollapse: (key: string) => void
  childMarkets: any[]
  collapsedMarketIds: string[]
  isGameOpen: boolean
}

export const MarketBlockItem: React.FC<MarketBlockItemProps> = ({
  typeId,
  childMarkets,
  collapsedMarketIds,
  onCollapse,
  market,
  isGameOpen,
}) => {
  const sortedChildMarkets = useMemo(
    () => orderBy(childMarkets, ['line', 'odds'], ['asc', 'desc']),
    [childMarkets]
  )

  const name = useMemo(
    () => getTitleText(sortedChildMarkets[0]),
    [sortedChildMarkets]
  )

  if (name.toLowerCase() === 'Total 1st map (aab esport)'.toLowerCase()) {
    console.log({ childMarkets })
  }
  const isCollapsed = collapsedMarketIds.includes(typeId.toString())

  return (
    <div className="px-2">
      <button
        className={cx(
          'flex items-center justify-between p-4 w-full group cursor-pointer bg-[#0F0E0D]',
          {
            'border-b border-b-accent-grey-1000': isCollapsed,
          }
        )}
        onClick={() => onCollapse(typeId.toString())}
      >
        <div className="flex items-center">
          <div className="text-caption-14 font-semibold">{name}</div>
        </div>
        <div className="px-2 bg-accent-grey-1000 text-accent-grey-200 group-hover:bg-accent-grey-800 group-hover:text-accent-grey-30 rounded-ssm">
          <Icon
            className="size-4"
            name={
              isCollapsed ? 'interface/chevron_down' : 'interface/chevron_up'
            }
          />
        </div>
      </button>

      {!isCollapsed && (
        <div className="space-y-2 bg-[#1B1A17] rounded-sm p-2">
          {sortedChildMarkets.map((condition, index) => (
            <Condition
              key={`${condition.gameId}-${condition.typeId}-${condition.line}-${condition.playerProps.playerId}-${index}`}
              condition={condition}
              marketName={name}
              market={market}
              isGameOpen={isGameOpen}
            />
          ))}
        </div>
      )}
    </div>
  )
}

type MarketsProps = {
  market: SportMarket | any
  gameState: GameState
}

// const ResolvedMarkets: React.FC<MarketsProps> = ({ markets, gameState }) => {
//   const { address } = useAccount()
//   const { data: markets, isLoading } = useResolvedMarkets({
//     gameId: game.gameId,
//   })
//   const { data: betsSummary } = useBetsSummaryBySelection({
//     account: address!,
//     gameId: game.gameId,
//     gameState,
//   })

//   if (isLoading) {
//     return <MarketsSkeleton />
//   }

//   if (!markets?.length) {
//     return <div>Empty</div>
//   }

//   return (
//     <Content markets={markets} game={game} betsSummary={betsSummary} isResult />
//   )
// }
const WAIT_TIME = 600000

const ActiveMarkets: React.FC<MarketsProps> = ({ market, gameState }) => {
  const [hidePausedMarkets, setHidePausedMarkets] = useState(true)

  const groupedChildMarkets = useMemo(
    () =>
      groupBy(
        market.childMarkets.filter(childMarket =>
          hidePausedMarkets && market.isOpen && market.maturityDate > new Date()
            ? !childMarket.isPaused
            : true
        ),
        childMarket => childMarket.typeId
      ),
    [market.childMarkets, market.isOpen, market.maturityDate, hidePausedMarkets]
  )

  // const numberOfMarkets = useMemo(() => {
  //   let num = 1 // Start with 1 for the main market
  //   Object.keys(groupedChildMarkets).forEach(key => {
  //     const typeId = Number(key)
  //     const childMarkets = groupedChildMarkets[typeId]
  //     num += childMarkets.length
  //   })
  //   return num
  // }, [groupedChildMarkets])

  const isGameStarted = market.maturityDate < new Date()
  const isGameOpen = market.isOpen && !isGameStarted
  const isGameResolved = market.isResolved || market.isCancelled
  // const isPendingResolution = isGameStarted && !isGameResolved
  // const showStatus = !market.isOpen || isGameStarted
  const liveScore = market.liveScore

  // const leagueSport = getLeagueSport(market.leagueId)

  // const isLiveScoreStatusHalfTime =
  //   liveScore &&
  //   (liveScore.gameStatus == GameStatus.RUNDOWN_HALF_TIME ||
  //     liveScore.gameStatus == GameStatus.OPTICODDS_HALF)

  // const {
  //   data: markets,
  //   isLoading,
  //   isPlaceholderData,
  // } = useActiveMarkets({
  //   gameId: game.id,
  //   query: {
  //     refetchInterval: 10_000,
  //   },
  // })

  // const isLive = gameState === GameState.Live

  // const startDate = +game.startsAt * 1000
  // const shouldWait = () => isLive && dayjs().diff(startDate) < WAIT_TIME
  // const [waitingTime, setWaitingTime] = useState(
  //   shouldWait() ? WAIT_TIME - dayjs().diff(startDate) : 0
  // )

  // useEffect(() => {
  //   if (shouldWait() && !markets?.length) {
  //     const interval = setInterval(() => {
  //       const newWaitingTime = Math.max(WAIT_TIME - dayjs().diff(startDate), 0)

  //       if (newWaitingTime === 0) {
  //         clearInterval(interval)
  //       }
  //       setWaitingTime(newWaitingTime)
  //     }, 1000)

  //     return () => {
  //       clearInterval(interval)
  //     }
  //   }
  // }, [gameState, markets])

  // if (isLoading || isPlaceholderData) {
  //   return <MarketsSkeleton />
  // }

  // if (!markets) {
  //   if (isLive) {
  //     if (waitingTime) {
  //       const time = dayjs.duration(waitingTime).format('mm:ss')

  //       return (
  //         <EmptyContent
  //           image="/images/illustrations/smile_sad.png"
  //           title={messages.livePending.title}
  //           text={{ ...messages.livePending.text, values: { time } }}
  //         />
  //       )
  //     } else {
  //       return (
  //         <EmptyContent
  //           image="/images/illustrations/smile_sad.png"
  //           title={messages.empty.live.title}
  //           text={messages.empty.live.text}
  //         />
  //       )
  //     }
  //   }

  //   return (
  //     <EmptyContent
  //       className="py-20"
  //       image="/images/illustrations/smile_sad.png"
  //       title={messages.empty.prematch.title}
  //       text={messages.empty.prematch.text}
  //     />
  //   )
  // }

  return <Content market={market} groupedChildMarkets={groupedChildMarkets} />
}

const OvertimeMarkets: React.FC<MarketsProps> = props => {
  const { gameState, market } = props

  const isGameStarted = market.maturityDate < new Date()
  const isGameResolved = market.isResolved || market.isCancelled
  const isPendingResolution = isGameStarted && !isGameResolved

  // TODO
  // if (market.isGameFinished) {
  //   return <div>ResolvedMarkets</div>
  //   // return <ResolvedMarkets {...props} />
  // }

  // if (isPendingResolution) {
  //   return (
  //     <EmptyContent
  //       className="py-20"
  //       image="/images/illustrations/smile_sad.png"
  //       title={messages.ended.title}
  //       text={messages.ended.text}
  //     />
  //   )
  // }

  return <ActiveMarkets {...props} />
}

export default OvertimeMarkets
